import React from 'react';
import { Target, Users, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Our Mission',
      description: 'To provide superior quality copper products that power industries and build lasting relationships with our customers through reliability and excellence.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Customer First',
      description: 'We prioritize customer satisfaction by offering personalized service, competitive pricing, and maintaining the highest quality standards in every product.'
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Quality Commitment',
      description: 'Every product we supply undergoes rigorous quality checks to ensure it meets industry standards and exceeds customer expectations.'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Industry Leadership',
      description: 'With years of experience in the copper trading industry, we have established ourselves as a trusted and reliable partner for businesses across sectors.'
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-slate-900 pt-20 transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/10040002/pexels-photo-10040002.jpeg?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 dark:from-slate-900 via-gray-50/95 dark:via-slate-900/95 to-gray-50 dark:to-slate-900"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              About <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">CopperAxis</span>
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
              Building the future of copper supply with strength, structure, and reliability at our core.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Excellence Engineered in
                <span className="block bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  Every Connection
                </span>
              </h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  CopperAxis is a premium brand developed and operated by Mehra Mac Industries Pvt. Ltd.
                </p>
                <p>
                  Representing a new era in copper supply excellence, CopperAxis leverages the 17 years of proven expertise 
                  from Mehra Mac Industries and the 40+ years of industry knowledge from our founding owners.
                </p>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  "We built CopperAxis around three principles - strength in quality, structured operations, and dependable supply."
                </p>
                <p>
                  <strong className="text-gray-900 dark:text-white">Strength in Material:</strong> Every copper product we supply undergoes rigorous quality testing to ensure it 
                  meets the highest industry standards. We source only premium-grade copper, ensuring superior conductivity and durability.
                </p>
                <p>
                  <strong className="text-gray-900 dark:text-white">Structure in Process:</strong> Our operations are built on systematic processes and proven methodologies. 
                  From procurement to delivery, every step is optimized for efficiency and precision.
                </p>
                <p>
                  <strong className="text-gray-900 dark:text-white">Reliability in Supply:</strong> We understand that your operations depend on timely delivery. 
                  Our robust supply chain and strategic inventory management ensure you get what you need, when you need it.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_coppertrade/artifacts/pia726t5_Gemini_Generated_Image_ypxgxrypxgxrypxg.png" 
                  alt="Industrial facility"
                  className="w-full h-96 object-cover rounded-xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/20 to-transparent rounded-xl"></div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-cyan-500 to-blue-600 p-8 rounded-xl shadow-2xl border-4 border-white dark:border-slate-900">
                <div className="text-white text-center">
                  <div className="text-4xl font-bold">40+</div>
                  <div className="text-sm">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">Values</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="group p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-950 dark:to-slate-900 rounded-xl border border-gray-200 dark:border-amber-600/20 hover:border-amber-500/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="text-amber-400 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center">
              Why <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">Choose Us?</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Premium Quality', desc: 'All products meet industry standards' },
                { title: 'Competitive Pricing', desc: 'Best market rates with flexibility' },
                { title: 'Expert Team', desc: 'Knowledgeable staff to assist you' },
                { title: 'Timely Delivery', desc: 'Efficient logistics and supply chain' },
                { title: 'Wide Range', desc: 'Comprehensive product portfolio' },
                { title: 'Customer Support', desc: 'Dedicated service before and after sale' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-4 p-6 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-amber-600/20 hover:border-amber-500/50 transition-all duration-300">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-gray-900 dark:text-white font-semibold mb-1">{item.title}</h4>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;